import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import QOLForm from "./QOLForm";
import { fetchQolAssessment, updateQolAssessment } from "../../../store/slices/qolAssessmentsSlice";

const EditQOL = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        clientName: "",
        diagnosis: "",
        assessedBy: "",
        q1: "", q2: "", q3: "", q4: "", q5: "", q6: "", q7: "", q8: "",
        q9: "", q10: "", q11: "", q12: "", q13: "", q14: "", q15: "", q16: ""
    });

    useEffect(() => {
        const loadAssessment = async () => {
            try {
                const res = await dispatch(fetchQolAssessment(id)).unwrap();
                const data = res;

                // Map backend snake_case fields to frontend keys
                setFormData({
                    caseId: data.case_id || "",
                    patientId: data.patient_id || "",
                    clientName: data.client_name || "",
                    diagnosis: data.diagnosis || "",
                    assessedBy: data.assessed_by || "",
                    q1: String(data.q1_material_score || ""),
                    q2: String(data.q2_health_score || ""),
                    q3: String(data.q3_relationship_score || ""),
                    q4: String(data.q4_having_score || ""),
                    q5: String(data.q5_spouse_score || ""),
                    q6: String(data.q6_friends_score || ""),
                    q7: String(data.q7_helping_score || ""),
                    q8: String(data.q8_participation_score || ""),
                    q9: String(data.q9_learning_score || ""),
                    q10: String(data.q10_selfunderstanding_score || ""),
                    q11: String(data.q11_work_score || ""),
                    q12: String(data.q12_creativity_score || ""),
                    q13: String(data.q13_socializing_score || ""),
                    q14: String(data.q14_entertainment_score || ""),
                    q15: String(data.q15_recreation_score || ""),
                    q16: String(data.q16_independence_score || ""),
                });
            } catch (err) {
                Swal.fire("Error", "Failed to fetch QOL assessment", "error");
            }
        };

        loadAssessment();
    }, [dispatch, id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const calculateTotal = () => {
        return Object.keys(formData)
            .filter(k => k.startsWith("q"))
            .reduce((sum, key) => sum + parseInt(formData[key] || 0, 10), 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const totalScore = calculateTotal();

        if (totalScore < 16) {
            Swal.fire("Incomplete", "Please complete all 16 items.", "warning");
            return;
        }

        const options = [
            { value: "7", label: "Delighted" },
            { value: "6", label: "Pleased" },
            { value: "5", label: "Mostly Satisfied" },
            { value: "4", label: "Mixed" },
            { value: "3", label: "Mostly Dissatisfied" },
            { value: "2", label: "Unhappy" },
            { value: "1", label: "Terrible" }
        ];

        const questionMap = [
            { scoreKey: "q1_material_score", valueKey: "q1_material_value", formKey: "q1" },
            { scoreKey: "q2_health_score", valueKey: "q2_health_value", formKey: "q2" },
            { scoreKey: "q3_relationship_score", valueKey: "q3_relationship_value", formKey: "q3" },
            { scoreKey: "q4_having_score", valueKey: "q4_having_value", formKey: "q4" },
            { scoreKey: "q5_spouse_score", valueKey: "q5_spouse_value", formKey: "q5" },
            { scoreKey: "q6_friends_score", valueKey: "q6_friends_value", formKey: "q6" },
            { scoreKey: "q7_helping_score", valueKey: "q7_helping_value", formKey: "q7" },
            { scoreKey: "q8_participation_score", valueKey: "q8_participation_value", formKey: "q8" },
            { scoreKey: "q9_learning_score", valueKey: "q9_learning_value", formKey: "q9" },
            { scoreKey: "q10_selfunderstanding_score", valueKey: "q10_selfunderstanding_value", formKey: "q10" },
            { scoreKey: "q11_work_score", valueKey: "q11_work_value", formKey: "q11" },
            { scoreKey: "q12_creativity_score", valueKey: "q12_creativity_value", formKey: "q12" },
            { scoreKey: "q13_socializing_score", valueKey: "q13_socializing_value", formKey: "q13" },
            { scoreKey: "q14_entertainment_score", valueKey: "q14_entertainment_value", formKey: "q14" },
            { scoreKey: "q15_recreation_score", valueKey: "q15_recreation_value", formKey: "q15" },
            { scoreKey: "q16_independence_score", valueKey: "q16_independence_value", formKey: "q16" }
        ];

        // Build payload
        const payload = {
            case_id: caseId,
            patient_id: patientId,
            client_name: formData.clientName,
            diagnosis: formData.diagnosis,
            assessed_by: formData.assessedBy,
        };

        questionMap.forEach(q => {
            const score = formData[q.formKey];
            const value = options.find(opt => opt.value === score)?.label || "";
            payload[q.scoreKey] = score;
            payload[q.valueKey] = value;
        });

        try {
            await dispatch(updateQolAssessment({ case_id: caseId, assessmentData: payload })).unwrap();
            Swal.fire("Updated!", "QOLS assessment updated successfully.", "success");
            // Stay on edit page
        } catch (err) {
            Swal.fire("Error", "Failed to update QOLS assessment", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase04/qol/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <QOLForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i>
                                        Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditQOL;
